import { useGetAuthMe } from "@birdwatch/client";
import {
  BlockScroll,
  Modal,
  ModalContent,
  ModalDescription,
  ModalHeader,
  ModalTitle,
} from "@birdwatch/ui";
import { toast } from "@birdwatch/ui/toast";
import { cn } from "cn";
import { Button } from "@/components/ui/button/button";
import { copyToast } from "@/components/ui/common/copy";
import {
  BwEmailLine,
  BwLogoWithText,
  BwMessagesLine,
} from "@/components/ui/icons";
import { env } from "@/env";

type ReferAMemberDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

export function ReferAMemberDialog({
  open,
  onOpenChange,
}: ReferAMemberDialogProps) {
  const { data: me } = useGetAuthMe().get();

  // Construct refer link - using referId from me data
  const baseUrl = `${env.PUBLIC_LINK_APP}/r`;
  const referUrl = me?.referId ? `${baseUrl}/${me.referId}` : baseUrl;

  const handleCopy = () => {
    if (!me?.referId) {
      toast.error({
        title: "Referral link not available.",
      });
      return;
    }

    copyToast(referUrl);
  };

  const mailSubject = encodeURIComponent("Check out Birdwatch!");
  const mailBody = encodeURIComponent(
    `I think you'll love Birdwatch - it's a new type of membership that simplifies home upkeep, maintenance and projects. You get a dedicated service coordinator, monthly service allowance, access to a team of skill technicians, a custom home care plan, and an app to track and plan your to-do list. Use my link below to join and you'll get free onboarding AND a complimentary Home Evaluation visit & report (savings of up to $798). \n${referUrl}`,
  );

  return (
    <Modal open={open} onOpenChange={onOpenChange}>
      <ModalContent
        className={cn(
          /* sorted */
          "flex flex-col",
          "gap-0 p-0",
          "sm:max-w-md",
        )}
      >
        <div className="flex flex-col overflow-hidden sm:max-h-[94vh]">
          <ModalHeader className="text-left">
            <ModalTitle className="p-0 text-xl font-semibold -mt-1">
              Refer a Member
            </ModalTitle>

            <ModalDescription className="sr-only">
              Refer a member and earn rewards
            </ModalDescription>
          </ModalHeader>

          <BlockScroll>
            <div className="p-4 pt-1">
              <div className="flex flex-col gap-6">
                <div className="mx-auto h-36 w-60 overflow-hidden rounded-2xl border border-border-dark-low">
                  <div className="flex h-full w-full items-center justify-center bg-linear-to-br from-bg-primary-min to-bg-indigo-max shadow-lg [&_svg_path]:fill-text-light [&_svg]:fill-text-light">
                    <BwLogoWithText className="h-8 w-32 fill-text-light text-text-light" />
                  </div>
                </div>

                <div className="flex flex-col gap-1">
                  <p className="text-center text-xl font-semibold leading-7 text-text-dark">
                    <span role="img" aria-label="">
                      💸
                    </span>{" "}
                    Earn a free month for every new <br /> member you refer{" "}
                    <span role="img" aria-label="">
                      🔥
                    </span>
                  </p>
                  <p className="text-center text-sm font-normal leading-normal text-text-dark-high">
                    Invite your family, friends & neighbors to join Birdwatch.
                    When they enroll, you get a free month of membership and
                    they get free onboarding plus a Home Evaluation.
                  </p>
                </div>

                <div className="flex items-center justify-between gap-4 rounded-2xl border border-dashed border-border-dark-low bg-bg-dark-min px-4 py-3">
                  <p className="line-clamp-1 grow break-all text-base font-normal leading-normal text-text-dark">
                    {referUrl}
                  </p>
                  <Button onClick={handleCopy} disabled={!me?.referId}>
                    Copy Link
                  </Button>
                </div>

                <div className="divide-y divide-border-dark-low rounded-2xl border border-border-dark-low">
                  <a
                    href={`mailto:?subject=${mailSubject}&body=${mailBody}`}
                    className="block"
                  >
                    <div className="flex items-center gap-4 px-4 py-3 text-text-dark">
                      <BwEmailLine size={24} />
                      <p className="text-base font-normal leading-normal">
                        Email
                      </p>
                    </div>
                  </a>
                  <a href={`sms:///&body=${mailBody}`} className="block">
                    <div className="flex items-center gap-4 px-4 py-3 text-text-dark">
                      <BwMessagesLine size={24} />
                      <p className="text-base font-normal leading-normal">
                        SMS
                      </p>
                    </div>
                  </a>
                </div>

                <div className="rounded-2xl bg-bg-dark-min">
                  <p className="p-4 text-xl font-semibold leading-7 text-text-dark">
                    How does it work?
                  </p>
                  <div className="divide-y divide-border-dark-low">
                    <div className="px-4 py-3">
                      <p className="text-base font-normal leading-normal text-text-dark">
                        1. Refer busy homeowners
                      </p>
                      <p className="text-sm font-normal leading-5 text-text-dark-high">
                        Tell your family, friends and neighbors how Birdwatch
                        can unlock their home to-do list.
                      </p>
                    </div>
                    <div className="px-4 py-3">
                      <p className="text-base font-normal leading-normal text-text-dark">
                        2. New members
                      </p>
                      <p className="text-sm font-normal leading-5 text-text-dark-high">
                        Your code grants them free onboarding and a bonus Home
                        Evaluation service.
                      </p>
                    </div>
                    <div className="px-4 py-3">
                      <p className="text-base font-normal leading-normal text-text-dark">
                        3. Earn free months
                      </p>
                      <p className="text-sm font-normal leading-5 text-text-dark-high">
                        When your referral completes onboarding, you'll get your
                        next membership payment credited.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </BlockScroll>
        </div>
      </ModalContent>
    </Modal>
  );
}
